import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  Copy as CopyIcon,
  ExternalLink,
  Facebook,
  Globe,
  MapPin,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  SkipForward,
  ThumbsDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  TelnyxBrowserCall,
  TelnyxBrowserCallError,
  TERMINAL_TELNYX_CALL_STATES,
} from '@/lib/telnyxWebrtc';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
  Textarea,
} from '@/components/ui/primitives';
import { Tooltip } from '@/components/ui/overlays';
import { FollowUpDialog } from '@/components/FollowUpDialog';
import {
  AudioDeviceSetup,
  loadAudioDevicePreferences,
  type AudioDevicePreferences,
} from '@/components/AudioDeviceSetup';
import {
  cn,
  copyToClipboard,
  formatDateTime,
  formatPhone,
  formatRelative,
  formatBusinessAge,
  pipelineStatusClass,
  scoreClass,
  severityClass,
  todayLocalDate,
  addDaysLocal,
  webPresenceClass,
} from '@/lib/utils';
import { FOLLOW_UP_TYPES } from '@shared/enums.js';
import { toE164Us } from '@shared/schemas.js';
import type { Lead } from '@shared/types.js';

function callStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    idle: 'Ready',
    connecting: 'Connecting browser audio',
    new: 'Starting call',
    trying: 'Dialing',
    requesting: 'Dialing',
    early: 'Ringing',
    ringing: 'Ringing',
    active: 'Connected',
    held: 'On hold',
    hangup: 'Call ended',
    destroy: 'Call ended',
    purge: 'Call ended',
    fallback: 'Phone app fallback',
    failed: 'Browser call failed',
  };
  return labels[status] ?? status;
}

/**
 * The calling workspace. Optimised for high-volume dialling: one lead at a
 * time, big phone number, everything needed on one screen, keyboard-driven.
 */
export function CallingWorkspacePage() {
  const { sessionId: rawSessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // /queue/lead-123 opens a single lead outside any queue.
  const singleLeadId = rawSessionId?.startsWith('lead-') ? Number(rawSessionId.slice(5)) : null;
  const sessionId = singleLeadId ? null : Number(rawSessionId);

  const [notes, setNotes] = React.useState('');
  const [outcomeId, setOutcomeId] = React.useState<number | ''>('');
  const [callStartedAt, setCallStartedAt] = React.useState<number | null>(null);
  const [browserCallStatus, setBrowserCallStatus] = React.useState('idle');
  const [isMuted, setIsMuted] = React.useState(false);
  const [followUpOpen, setFollowUpOpen] = React.useState(false);
  const [scheduleFollowUp, setScheduleFollowUp] = React.useState(false);
  const [followUpType, setFollowUpType] = React.useState('Call');
  const [followUpDate, setFollowUpDate] = React.useState(addDaysLocal(todayLocalDate(), 3));
  const browserCallRef = React.useRef<TelnyxBrowserCall | null>(null);
  const callAttemptRef = React.useRef(0);
  const callStartingRef = React.useRef(false);
  const remoteAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const [audioPreferences, setAudioPreferences] = React.useState<AudioDevicePreferences>(() =>
    loadAudioDevicePreferences(),
  );

  const outcomes = useQuery({ queryKey: ['call-outcomes'], queryFn: api.callOutcomes });

  const queueQuery = useQuery({
    queryKey: ['queue-next', sessionId],
    queryFn: () => api.nextInQueue(sessionId as number),
    enabled: !!sessionId,
  });
  const singleQuery = useQuery({
    queryKey: ['calling-lead', singleLeadId],
    queryFn: () => api.callingLead(singleLeadId as number),
    enabled: !!singleLeadId,
  });

  const payload = sessionId ? queueQuery.data : singleQuery.data;
  const workspace = payload?.lead ?? null;
  const isLoading = sessionId ? queueQuery.isLoading : singleQuery.isLoading;
  const error = sessionId ? queueQuery.error : singleQuery.error;

  const disposeBrowserCall = React.useCallback(() => {
    callAttemptRef.current += 1;
    callStartingRef.current = false;
    const browserCall = browserCallRef.current;
    browserCallRef.current = null;
    if (browserCall) void browserCall.dispose().catch(() => undefined);
  }, []);

  // Reset the form whenever a new lead is presented.
  React.useEffect(() => {
    disposeBrowserCall();
    setNotes('');
    setOutcomeId('');
    setCallStartedAt(null);
    setBrowserCallStatus('idle');
    setIsMuted(false);
    setScheduleFollowUp(false);
    setFollowUpDate(addDaysLocal(todayLocalDate(), 3));
  }, [disposeBrowserCall, workspace?.lead.id]);

  React.useEffect(() => () => disposeBrowserCall(), [disposeBrowserCall]);

  const record = useMutation({
    mutationFn: () => {
      if (!workspace || outcomeId === '') throw new Error('Choose an outcome first');
      return api.recordCall({
        leadId: workspace.lead.id,
        phoneNumber: workspace.lead.normalizedPhone ?? workspace.lead.primaryPhone ?? '',
        outcomeId: Number(outcomeId),
        startedAt: callStartedAt ?? undefined,
        notes: notes.trim() || null,
        sessionId: sessionId ?? undefined,
        followUp: scheduleFollowUp
          ? { followUpType, dueDate: followUpDate, priority: 'Normal', notes: null }
          : null,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['my-leads'] });
      queryClient.invalidateQueries({ queryKey: ['my-callable-count'] });
      queryClient.invalidateQueries({ queryKey: ['queue-preview'] });

      const bits: string[] = [];
      if (result.appliedStatus) bits.push(`status → ${result.appliedStatus}`);
      if (result.appliedDnc) bits.push('Do Not Contact applied');
      if (result.followUpId) bits.push('follow-up scheduled');
      if (result.cooldownUntil) bits.push(`cooling down until ${formatDateTime(result.cooldownUntil)}`);
      toast.success(`Call recorded${bits.length ? ` — ${bits.join(', ')}` : ''}`);

      if (result.triggersCustomerConversion && workspace) {
        toast.info('Marked Sold — open the lead to record the sale and payments.', {
          action: { label: 'Open lead', onClick: () => navigate(`/leads/${workspace.lead.id}`) },
          duration: 10_000,
        });
      }

      if (sessionId) queueQuery.refetch();
      else singleQuery.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const skip = useMutation({
    mutationFn: () => {
      if (!workspace?.queueItemId) throw new Error('Nothing to skip');
      return api.skipQueueItem(workspace.queueItemId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
      queueQuery.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const applyDnc = useMutation({
    mutationFn: () => api.setLeadDnc(workspace!.lead.id, true, 'Requested during call'),
    onSuccess: () => {
      toast.success('Do Not Contact applied');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      if (sessionId) queueQuery.refetch();
      else singleQuery.refetch();
    },
  });

  const markBadLead = useMutation({
    mutationFn: () => api.setLeadStatus(workspace!.lead.id, 'Bad Lead'),
    onSuccess: () => {
      toast.success('Marked as a bad lead');
      if (sessionId) queueQuery.refetch();
      else singleQuery.refetch();
    },
  });

  const startFallbackCall = React.useCallback(() => {
    if (!workspace) return;
    setCallStartedAt(Math.floor(Date.now() / 1000));
    const uri = payload?.callUri;
    if (uri) {
      window.location.href = uri;
    } else {
      void copyToClipboard(workspace.lead.normalizedPhone ?? workspace.lead.primaryPhone ?? '').then((ok) => {
        if (ok) toast.success('Phone number copied (copy-only mode)');
      });
    }
  }, [workspace, payload?.callUri]);

  const startCall = React.useCallback(async () => {
    if (!workspace || browserCallRef.current || callStartingRef.current) return;

    const destinationNumber = toE164Us(workspace.lead.normalizedPhone ?? workspace.lead.primaryPhone ?? '');
    const remoteElement = remoteAudioRef.current;
    const browserAudioAvailable =
      typeof window.RTCPeerConnection !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      !!remoteElement;
    if (!browserAudioAvailable || !destinationNumber) {
      if (payload?.protocol === 'webrtc') {
        setBrowserCallStatus('failed');
        toast.error('Browser calling is unavailable on this device. LeadForge did not fall back to copy-only mode.');
      } else {
        setBrowserCallStatus('fallback');
        startFallbackCall();
      }
      return;
    }

    const attempt = ++callAttemptRef.current;
    callStartingRef.current = true;
    setBrowserCallStatus('connecting');
    setIsMuted(false);
    let browserCall: TelnyxBrowserCall | null = null;
    try {
      const { token, callerNumber, callerName } = await api.telnyxWebrtcToken();
      if (attempt !== callAttemptRef.current) return;

      browserCall = new TelnyxBrowserCall({
        token,
        destinationNumber,
        callerNumber,
        callerName: callerName || undefined,
        remoteElement,
        micId: audioPreferences.micId || undefined,
        speakerId: audioPreferences.speakerId || undefined,
        onDialStarted: () => {
          if (attempt === callAttemptRef.current) {
            setCallStartedAt(Math.floor(Date.now() / 1000));
          }
        },
        onUpdate: (snapshot) => {
          if (attempt !== callAttemptRef.current) return;
          setBrowserCallStatus(snapshot.status);
          setIsMuted(snapshot.isMuted);
          if (!snapshot.hasCall && TERMINAL_TELNYX_CALL_STATES.has(snapshot.status)) {
            browserCallRef.current = null;
          }
        },
        onError: (message) => {
          if (attempt !== callAttemptRef.current) return;
          browserCallRef.current = null;
          setBrowserCallStatus('failed');
          setIsMuted(false);
          toast.error(message);
        },
      });
      browserCallRef.current = browserCall;
      await browserCall.start();
    } catch (error) {
      if (attempt !== callAttemptRef.current) return;
      if (browserCallRef.current === browserCall) browserCallRef.current = null;

      const safeToFallback =
        !(error instanceof TelnyxBrowserCallError) || error.safeToFallback;
      if (safeToFallback && payload?.protocol !== 'webrtc') {
        setBrowserCallStatus('fallback');
        toast.warning(
          `Browser calling unavailable; opening your phone app instead. ${error instanceof Error ? error.message : ''}`.trim(),
        );
        startFallbackCall();
      } else {
        setBrowserCallStatus('failed');
        setIsMuted(false);
        toast.error(
          `${error instanceof Error ? error.message : 'Browser calling failed.'} No phone-app fallback was opened because the browser may already have started the call.`,
        );
      }
    } finally {
      if (attempt === callAttemptRef.current) callStartingRef.current = false;
    }
  }, [audioPreferences.micId, audioPreferences.speakerId, payload?.protocol, startFallbackCall, workspace]);

  const toggleMute = React.useCallback(() => {
    browserCallRef.current?.toggleMute();
  }, []);

  const hangupBrowserCall = React.useCallback(() => {
    const browserCall = browserCallRef.current;
    if (!browserCall) return;
    callAttemptRef.current += 1;
    callStartingRef.current = false;
    browserCallRef.current = null;
    setBrowserCallStatus('hangup');
    setIsMuted(false);
    void browserCall.hangup().catch((error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Could not hang up the browser call.');
    });
  }, []);

  // Keyboard shortcuts for the calling loop.
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        if (!((event.ctrlKey || event.metaKey) && event.key === 'Enter')) return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        if (outcomeId !== '') record.mutate();
        return;
      }
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key.toLowerCase() === 'c') {
        event.preventDefault();
        void startCall();
      } else if (event.key.toLowerCase() === 's' && workspace?.queueItemId) {
        event.preventDefault();
        skip.mutate();
      } else if (event.key.toLowerCase() === 'y') {
        event.preventDefault();
        void copyToClipboard(workspace?.lead.normalizedPhone ?? workspace?.lead.primaryPhone ?? '').then(
          (ok) => ok && toast.success('Phone copied'),
        );
      } else if (/^[1-9]$/.test(event.key)) {
        const list = outcomes.data?.outcomes.filter((o) => o.isActive) ?? [];
        const picked = list[Number(event.key) - 1];
        if (picked) {
          event.preventDefault();
          setOutcomeId(picked.id);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [outcomeId, record, skip, startCall, workspace, outcomes.data]);

  const browserCallInProgress =
    browserCallStatus === 'connecting' || !!browserCallRef.current;

  if (isLoading) return <Page title="Calling"><LoadingState /></Page>;
  if (error) {
    return (
      <Page title="Calling">
        <ErrorState error={error} onRetry={() => (sessionId ? queueQuery.refetch() : singleQuery.refetch())} />
      </Page>
    );
  }

  if (!workspace) {
    return (
      <Page title="Calling">
        <EmptyState
          icon={Phone}
          title="Queue finished"
          description="Every lead in this queue has been called, skipped, or removed because its state changed."
          action={
            <Button asChild size="sm">
              <Link to="/queue">Back to queues</Link>
            </Button>
          }
        />
      </Page>
    );
  }

  const lead = workspace.lead;
  const progress = sessionId ? queueQuery.data?.progress : null;
  const activeOutcomes = outcomes.data?.outcomes.filter((o) => o.isActive) ?? [];

  // Where they are in the session. Worked items plus this one, so the first lead
  // reads "1 of 60" rather than "0 of 60".
  const position = progress ? progress.consumed + progress.skipped + 1 : null;
  const total = progress?.session.totalItems ?? 0;
  const percent = progress && total ? Math.round(((position! - 1) / total) * 100) : 0;

  return (
    <Page
      title="Calling"
      description={
        progress
          ? `${progress.session.name} · lead ${position} of ${total}`
          : 'One lead, outside a queue'
      }
      noScroll
      actions={
        <>
          {progress && (
            <span className="hidden w-32 shrink-0 overflow-hidden rounded-full bg-muted md:block">
              <span className="block h-1.5 bg-primary transition-all" style={{ width: `${percent}%` }} />
            </span>
          )}
          <Button variant="ghost" size="sm" asChild>
            <Link to={sessionId ? '/queue' : `/leads/${lead.id}`}>
              <ArrowLeft className="h-3.5 w-3.5" />
              {sessionId ? 'My work' : 'Lead'}
            </Link>
          </Button>
          {workspace.queueItemId && (
            <Button variant="outline" size="sm" onClick={() => skip.mutate()}>
              <SkipForward className="h-3.5 w-3.5" />
              Skip
              <span className="kbd ml-1">S</span>
            </Button>
          )}
        </>
      }
    >
      {/*
        The approved layout. Everything needed during a live call is in one
        column down the middle — number, Call, outcome, notes, follow-up, save —
        and context sits to the right, where it is read before dialling rather
        than hunted for while somebody is talking.
      */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-5 md:p-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        {/* ---------------- The call ---------------- */}
        <div className="flex min-h-0 min-w-0 flex-col gap-3">
          <Card>
            <CardBody className="space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-2xl font-extrabold tracking-tight">{lead.businessName}</h2>
                  <p className="text-xs text-muted-foreground">
                    {[
                      [lead.city, lead.state].filter(Boolean).join(', '),
                      workspace.countyName && `${workspace.countyName} County`,
                      workspace.categoryName,
                      lead.businessStartedDate ? formatBusinessAge(lead.businessStartedDate) : null,
                      lead.rating ? `${lead.rating.toFixed(1)}★ (${lead.reviewCount ?? 0})` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {/*
                  Who to ask for. Stated on its own rather than buried in the
                  fact grid: it is the first thing said when the phone is picked
                  up, so it should not need looking for.
                */}
                {(lead.contactName || lead.ownerName) && (
                  <div className="shrink-0 text-right">
                    <p className="text-2xs uppercase tracking-wider text-muted-foreground">Ask for</p>
                    <p className="text-sm font-medium">{lead.contactName ?? lead.ownerName}</p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-1.5">
                  <Badge className={cn('border', pipelineStatusClass(lead.pipelineStatus))}>
                    {lead.pipelineStatus}
                  </Badge>
                  <Badge className={cn('border', webPresenceClass(lead.webPresenceStatus))}>
                    {lead.webPresenceStatus}
                  </Badge>
                  {lead.normalizedLeadScore !== null && (
                    <Badge className="border border-border">
                      <span className={scoreClass(lead.normalizedLeadScore)}>Score {lead.normalizedLeadScore}</span>
                    </Badge>
                  )}
                  {/* Whether this is a first approach or a repeat attempt. */}
                  <Badge variant="outline">
                    {lead.callAttemptCount
                      ? `${lead.callAttemptCount} prior ${lead.callAttemptCount === 1 ? 'call' : 'calls'}`
                      : 'Never called'}
                  </Badge>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <span className="phone-xl">{formatPhone(lead.primaryPhone)}</span>
                <Button onClick={() => void startCall()} size="lg" disabled={browserCallInProgress}>
                  <Phone className="h-4 w-4" />
                  Call
                </Button>
                {browserCallRef.current?.hasCall && (
                  <>
                    <Button variant="outline" onClick={toggleMute}>
                      {isMuted ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
                      {isMuted ? 'Unmute' : 'Mute'}
                    </Button>
                    <Button variant="destructive" onClick={hangupBrowserCall}>
                      <PhoneOff className="h-3.5 w-3.5" />
                      Hang up
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (await copyToClipboard(lead.normalizedPhone ?? lead.primaryPhone ?? '')) {
                      toast.success('Phone copied');
                    }
                  }}
                >
                  <CopyIcon className="h-3.5 w-3.5" />
                  Copy
                  <span className="kbd ml-1">Y</span>
                </Button>
                {callStartedAt && (
                  <Badge variant="primary">Call started {formatRelative(callStartedAt)}</Badge>
                )}
                {browserCallStatus !== 'idle' && (
                  <Badge variant={browserCallStatus === 'failed' ? 'danger' : 'outline'}>
                    {callStatusLabel(browserCallStatus)}
                    {isMuted ? ' · muted' : ''}
                  </Badge>
                )}
                <CallingFromBadge />
                <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
                <div className="w-full">
                  <AudioDeviceSetup
                    preferences={audioPreferences}
                    onChange={setAudioPreferences}
                    callInProgress={browserCallInProgress}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3 xl:grid-cols-4">
                <Info label="Owner" value={lead.ownerName} />
                <Info label="Contact" value={lead.contactName} />
                <Info label="Email" value={lead.email} />
                <Info label="Address" value={formatLeadAddress(lead)} />
                <Info label="Category" value={workspace.categoryName} />
                <Info
                  label="Counties served"
                  value={workspace.serviceCountyNames.length ? workspace.serviceCountyNames.join(', ') : null}
                />
                <Info
                  label="Service areas"
                  value={workspace.serviceAreas.length ? workspace.serviceAreas.join(', ') : null}
                />
                <Info
                  label="Rating"
                  value={lead.rating !== null ? `${lead.rating.toFixed(1)} from ${lead.reviewCount ?? 0} reviews` : null}
                />
                <Info
                  label="Business age"
                  value={lead.businessStartedDate ? formatBusinessAge(lead.businessStartedDate) : null}
                />
                <Info label="Prior attempts" value={String(lead.callAttemptCount ?? 0)} />
              </div>

              <div className="flex flex-wrap gap-1.5">
                {lead.googleBusinessProfileUrl && (
                  <QuickLink href={lead.googleBusinessProfileUrl} icon={MapPin} label="Google profile" />
                )}
                {lead.facebookUrl && <QuickLink href={lead.facebookUrl} icon={Facebook} label="Facebook" />}
                {lead.bbbUrl && <QuickLink href={lead.bbbUrl} icon={ExternalLink} label="BBB" />}
                {lead.yelpUrl && <QuickLink href={lead.yelpUrl} icon={ExternalLink} label="Yelp" />}
                {workspace.primaryWebsite && (
                  <QuickLink href={workspace.primaryWebsite.url} icon={Globe} label="Website" />
                )}
                {workspace.otherWebsites.map((website) => (
                  <QuickLink key={website.id} href={website.url} icon={Globe} label="Other website" />
                ))}
                {workspace.sources.map(
                  (source) =>
                    source.sourceUrl && (
                      <QuickLink
                        key={source.id}
                        href={source.sourceUrl}
                        icon={ExternalLink}
                        label={source.sourceTitle || source.sourceType}
                      />
                    ),
                )}
              </div>

              {workspace.primaryWebsite && (
                <div className="rounded border border-border px-2 py-1.5 text-xs">
                  <span className="text-muted-foreground">Website condition: </span>
                  <span className="font-medium">{workspace.primaryWebsite.condition}</span>
                </div>
              )}

            </CardBody>
          </Card>

          {/*
            The outcome form. Its body scrolls and the action row is pinned, so
            "Save & next" — a primary call control — can never be the thing that
            falls off the bottom of the screen at an enlarged scale.
          */}
          <Card className="flex min-h-0 flex-1 flex-col">
            <CardHeader>
              <CardTitle>Record the outcome</CardTitle>
            </CardHeader>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4">
              <div>
                <Label>Outcome — press 1–9</Label>
                <div className="mt-1 grid grid-cols-2 gap-1.5 lg:grid-cols-3">
                  {activeOutcomes.map((outcome, index) => (
                    <button
                      key={outcome.id}
                      onClick={() => setOutcomeId(outcome.id)}
                      className={cn(
                        'flex items-center gap-1.5 rounded border px-2 py-1.5 text-left text-xs transition-colors',
                        outcomeId === outcome.id
                          ? 'border-primary bg-primary/15 font-medium text-foreground'
                          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                    >
                      {index < 9 && <span className="kbd shrink-0">{index + 1}</span>}
                      <span className="truncate">{outcome.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Notes">
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What was said…"
                />
              </Field>

              <div>
                <Label>Follow-up</Label>
                {!scheduleFollowUp ? (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => setScheduleFollowUp(true)}>
                      Schedule with this outcome
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setFollowUpOpen(true)}>
                      More options
                    </Button>
                    <span className="text-2xs text-muted-foreground">
                      Add a callback/reminder only when this lead needs another touch.
                    </span>
                  </div>
                ) : (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded border border-border bg-muted/20 p-2">
                    <Select
                      value={followUpType}
                      onChange={(e) => setFollowUpType(e.target.value)}
                      className="h-7 w-28 text-xs"
                      aria-label="Follow-up type"
                    >
                      {FOLLOW_UP_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                    <Input
                      type="date"
                      min={todayLocalDate()}
                      value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                      className="h-7 w-36 text-xs"
                      aria-label="Follow-up date"
                    />
                    <Button variant="ghost" size="sm" onClick={() => setFollowUpOpen(true)}>
                      More options
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setScheduleFollowUp(false)}>
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              <p className="text-2xs leading-snug text-muted-foreground">
                LeadForge uses browser microphone/audio when Telnyx calling is configured. If setup
                fails before dialling, it safely keeps the existing phone-app handoff.
              </p>
              <p className="text-2xs leading-snug text-muted-foreground">
                <span className="kbd">C</span> call · <span className="kbd">Y</span> copy ·{' '}
                <span className="kbd">S</span> skip · <span className="kbd">1</span>–
                <span className="kbd">9</span> outcome · <span className="kbd">Ctrl ⏎</span> save
              </p>
            </div>

            {/* Pinned. Always on screen, at any scale. */}
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 border-t border-border bg-surface p-2.5">
              <Button variant="outline" size="sm" onClick={() => markBadLead.mutate()}>
                <ThumbsDown className="h-3.5 w-3.5" />
                Bad lead
              </Button>
              <Button variant="outline" size="sm" onClick={() => applyDnc.mutate()}>
                <Ban className="h-3.5 w-3.5" />
                Do not contact
              </Button>
              <Button
                disabled={outcomeId === ''}
                loading={record.isPending}
                onClick={() => record.mutate()}
              >
                Save &amp; next
                <span className="kbd ml-1">Ctrl ⏎</span>
              </Button>
            </div>
          </Card>
        </div>

        {/* ---------------- Context ---------------- */}
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto">
          {lead.outreachAngle && (
            <Card className="shrink-0 border-primary/30">
              <CardHeader>
                <CardTitle>Why we are calling</CardTitle>
              </CardHeader>
              <CardBody>
                <p className="whitespace-pre-wrap text-xs leading-snug">{lead.outreachAngle}</p>
              </CardBody>
            </Card>
          )}

          {lead.researchNotes && (
            <Card className="shrink-0">
              <CardHeader>
                <CardTitle>Research notes</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="max-h-64 overflow-y-auto pr-1">
                  <p className="whitespace-pre-wrap text-xs leading-relaxed">{lead.researchNotes}</p>
                </div>
              </CardBody>
            </Card>
          )}

          {workspace.topWebsiteIssues.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Website findings to mention</CardTitle>
              </CardHeader>
              <CardBody className="space-y-1.5">
                {workspace.topWebsiteIssues.map((issue) => (
                  <div key={issue.id} className="flex items-start gap-2 text-xs">
                    <Badge className={cn('border shrink-0', severityClass(issue.severity))}>{issue.severity}</Badge>
                    <div className="min-w-0">
                      <p className="font-medium">{issue.title ?? issue.issueTypeLabel}</p>
                      {issue.description && (
                        <p className="mt-0.5 whitespace-pre-wrap text-2xs leading-snug text-muted-foreground">
                          {issue.description}
                        </p>
                      )}
                      {issue.affectedUrl && (
                        <p className="mt-0.5 truncate text-2xs text-muted-foreground">{issue.affectedUrl}</p>
                      )}
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          {workspace.scoreReasons.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Why this lead scores {lead.normalizedLeadScore}</CardTitle>
              </CardHeader>
              <CardBody className="space-y-1.5">
                {workspace.scoreReasons.map((reason) => (
                  <div key={reason.id} className="flex items-start justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p>{reason.label}</p>
                      {reason.detail && (
                        <p className="whitespace-pre-wrap text-2xs leading-snug text-muted-foreground">
                          {reason.detail}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 font-semibold tabular-nums">
                      {reason.points > 0 ? '+' : ''}{reason.points}
                    </span>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Previous notes</CardTitle>
              </CardHeader>
              <CardBody className="space-y-2">
                {workspace.recentNotes.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No notes yet.</p>
                ) : (
                  workspace.recentNotes.map((note) => (
                    <div key={note.id} className="text-xs">
                      <p className="text-2xs text-muted-foreground">
                        {note.noteType} · {formatDateTime(note.createdAt)}
                      </p>
                      <p className="whitespace-pre-wrap">{note.noteBody}</p>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Previous calls</CardTitle>
              </CardHeader>
              <CardBody className="space-y-1.5">
                {workspace.recentCalls.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No calls yet.</p>
                ) : (
                  workspace.recentCalls.map((call) => (
                    <div key={call.id} className="rounded border border-border p-2 text-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{call.outcomeName}</Badge>
                        <span className="text-muted-foreground">{formatDateTime(call.startedAt)}</span>
                        {/* Who called and from which number, when we know. */}
                        {call.agentName && (
                          <span className="text-2xs text-muted-foreground">{call.agentName}</span>
                        )}
                        {call.lineLabel && (
                          <span className="text-2xs text-muted-foreground">via {call.lineLabel}</span>
                        )}
                      </div>
                      {call.notes && (
                        <p className="mt-1 whitespace-pre-wrap text-2xs leading-snug text-muted-foreground">
                          {call.notes}
                        </p>
                      )}
                    </div>
                  ))
                )}
                {workspace.openFollowUps.length > 0 && (
                  <div className="mt-2 border-t border-border pt-2">
                    <Label>Open follow-ups</Label>
                    {workspace.openFollowUps.map((fu) => (
                      <p key={fu.id} className="text-xs">
                        {fu.followUpType} due {fu.snoozedUntilDate ?? fu.dueDate}
                      </p>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

      </div>

      <FollowUpDialog
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        leadId={lead.id}
        defaultType={followUpType}
        defaultDate={followUpDate}
        onCreated={() => {
          // The dialog creates the follow-up immediately. Clear the inline
          // pending follow-up so Save & next cannot accidentally create a second.
          setScheduleFollowUp(false);
          void (sessionId ? queueQuery.refetch() : singleQuery.refetch());
        }}
      />
    </Page>
  );
}

function formatLeadAddress(lead: Lead): string | null {
  const cityState = [lead.city, lead.state].filter(Boolean).join(', ');
  const locality = [cityState || null, lead.zipCode].filter(Boolean).join(' ');
  const full = [lead.streetAddress, locality || null].filter(Boolean).join(', ');
  return full || null;
}

function Info({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <Label>{label}</Label>
      <p className="truncate">{value || <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}

function QuickLink({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Tooltip content={href}>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-2xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Icon className="h-3 w-3" />
        {label}
      </a>
    </Tooltip>
  );
}

/**
 * Which number this call goes out on.
 *
 * Read-only, and deliberately so: the line is derived server-side from the
 * caller's agent, and there is nothing here for a VA to configure. It exists
 * because someone on a call needs to be able to answer "what number did you
 * call from?" without asking the owner.
 *
 * Renders nothing when there is no phone-line inventory, or when the agent has
 * no active line. Calling works exactly as before in that case, and an empty
 * badge saying so would be noise.
 */
function CallingFromBadge(): JSX.Element | null {
  const line = useQuery({
    queryKey: ['my-phone-line'],
    queryFn: api.myPhoneLine,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const value = line.data?.line;
  if (!value) return null;

  return (
    <Tooltip
      content={`${value.label}${value.provider ? ` · ${value.provider}` : ''} · Browser audio is used for an active Telnyx line; otherwise LeadForge keeps the configured phone-app handoff.`}
    >
      <Badge className="border border-border">
        Calling from {value.e164 ? formatPhone(value.e164.replace('+1', '')) : value.label}
      </Badge>
    </Tooltip>
  );
}
